import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useLazyQuery, useMutation } from '@apollo/client';
import { Button, Progress } from 'reactstrap';
import * as tus from 'tus-js-client';
import { CREATE_BUNNY_VIDEO, GET_TUS_PRESIGNED_SIGNATURE } from '../../gqls';
import MayarAlert from '../alert/alert';
import Icon from '../icon';

const VideoDrmUpload = forwardRef(({ onUploaded, onLoading = () => {} }, ref) => {
  const [pickedFile, setPickedFile] = useState(null);
  const [maxBytes, setMaxBytes] = useState(0);
  const [currentBytesUploaded, setCurrentBytesUploaded] = useState(0);
  const [uploadStatus, setUploadStatus] = useState(''); // selected | uploading | error | success | exceeding_limit | invalid_type

  const [getPresignedSignature] = useLazyQuery(GET_TUS_PRESIGNED_SIGNATURE);
  const [createVideoBunny] = useMutation(CREATE_BUNNY_VIDEO);

  useEffect(() => {
    onLoading(uploadStatus === 'uploading');
  }, [uploadStatus]);

  const closeAlert = () => {
    setUploadStatus('');
    setPickedFile(null);
    onUploaded({ url: '', fileId: null });
  };

  const handleTusResumableUpload = (file, signature = {}, createResponse = {}) =>
    new Promise((resolve, reject) => {
      if (!signature.signature || !signature.expirationTime) {
        setUploadStatus('error');
        reject(new Error('Missing TUS presigned signature'));
        return;
      }

      const upload = new tus.Upload(file, {
        endpoint: process.env.REACT_APP_BUNNY_TUS_URL,
        retryDelays: [0, 3000, 5000, 10000, 20000, 60000, 60000],
        headers: {
          AuthorizationSignature: signature.signature,
          AuthorizationExpire: signature.expirationTime,
          VideoId: createResponse.bunnyVideoId,
          LibraryId: createResponse.bunnyLibraryId
        },
        metadata: {
          filetype: file.type,
          title: file.title
        },
        onError: err => {
          setUploadStatus('error');
          reject(err);
        },
        onProgress: (bytesUploaded, bytesTotal) => {
          setUploadStatus('uploading');

          setMaxBytes(bytesTotal);
          setCurrentBytesUploaded(bytesUploaded);
        },
        onSuccess: () => {
          const url = `https://video.bunnycdn.com/play/${createResponse.bunnyLibraryId}/${createResponse.bunnyVideoId}`;
          // createResponse.id is a real File row createBunnyVideo already
          // made (with bunnyVideoId/bunnyLibraryId set correctly) - the
          // caller should edit that File, not insert a new, unrelated one.
          const result = { url, fileId: createResponse.id };
          setUploadStatus('success');
          onUploaded(result);
          resolve(result);
        }
      });

      // No findPreviousUploads()/resumeFromPreviousUpload() here on purpose:
      // every startUpload() call mints a brand new Bunny video (fresh
      // VideoId/LibraryId/signature) via createVideoBunny, so a same-named
      // file picked again never has a legitimately resumable session -
      // tus-js-client's browser-cached "previous upload" is fingerprinted by
      // file name/size/type only, not our video id, so trying to resume it
      // just HEAD-requests a stale/unrelated upload URL that 404s and kills
      // the whole upload.
      upload.start();
    });

  // Called by the parent form's onSubmit, not on file drop - the actual Bunny
  // upload only starts once the merchant submits the whole form.
  const startUpload = async () => {
    if (!pickedFile) return null;

    try {
      setUploadStatus('uploading');

      const createVideo = await createVideoBunny({
        variables: {
          filename: pickedFile.name,
          type: pickedFile.type
        }
      });

      const res = createVideo?.data?.createBunnyVideo || {};

      if (!res.bunnyVideoId) {
        setUploadStatus('error');
        throw new Error('Failed to create Bunny video');
      }

      const dataSignature = await getPresignedSignature({
        variables: { videoId: res.bunnyVideoId }
      });

      const signatureResult = dataSignature?.data?.getTusPresignedSignature;

      return await handleTusResumableUpload(pickedFile, signatureResult, res);
    } catch (err) {
      setUploadStatus('error');
      throw err;
    }
  };

  useImperativeHandle(ref, () => ({
    startUpload,
    hasPendingFile: () => !!pickedFile
  }));

  const onDrop = (acceptedFiles = [], rejectedFiles = []) => {
    if (rejectedFiles.length) {
      setUploadStatus('invalid_type');
      return;
    }

    const file = acceptedFiles[0];

    if (!file) return;

    if (!file.type.startsWith('video/')) {
      setUploadStatus('invalid_type');
      return;
    }

    // We get file size in bytes and limit it to 10 gb
    if (file.size > 10000000000) {
      setUploadStatus('exceeding_limit');
      return;
    }

    setPickedFile(file);
    setUploadStatus('selected');
  };

  const { getRootProps, getInputProps } = useDropzone({ onDrop, accept: 'video/*' });

  return (
    <div>
      <div className="mb-7">
        <div className="mb-7">
          <strong>Video Hosting &amp; DRM Protection by Mayar</strong>
        </div>
        <p>
          Video terproteksi DRM sistem sehingga meminimalisir resiko pembajakan. Gratis, tidak ada biaya
          tambahan untuk penyimpanan maupun streaming video.
        </p>
      </div>

      {!uploadStatus && (
        <div {...getRootProps()} className="cursor-pointer w-100">
          <input {...getInputProps()} />
          <div className="w-100 d-flex justify-content-center align-items-center box-drop-zone">
            Drag &amp; Drop / klik disini untuk upload video
          </div>
        </div>
      )}

      {uploadStatus === 'selected' && pickedFile && (
        <div className="w-100 d-flex justify-content-between align-items-center box-drop-zone px-10">
          <span>{pickedFile.name}</span>
          <Button size="sm" color="light" onClick={closeAlert}>
            <Icon name="x" />
          </Button>
        </div>
      )}

      {uploadStatus === 'uploading' && <Progress animated color="primary" value={currentBytesUploaded} max={maxBytes} />}

      {uploadStatus === 'success' && <MayarAlert isSuccess message="Video berhasil diupload!" toggle={closeAlert} />}

      {uploadStatus === 'error' && <MayarAlert isError message="Video gagal diupload!" toggle={closeAlert} />}

      {uploadStatus === 'exceeding_limit' && (
        <MayarAlert isError message="Video gagal diupload karena melebih batas 10 GB!" toggle={closeAlert} />
      )}

      {uploadStatus === 'invalid_type' && (
        <MayarAlert isError message="File yang dipilih bukan video. Hanya file video yang diperbolehkan di sini." toggle={closeAlert} />
      )}

      <div className="mt-10">
        <small>
          {uploadStatus === 'selected'
            ? 'Video akan diupload saat kamu menekan tombol Buat/Update Produk Digital.'
            : 'File video yang diupload membutuhkan waktu untuk encoding dan memproses DRM. Silahkan tunggu beberapa saat setelah upload jika video anda belum muncul. Semakin besar ukuran file, semakin lama proses encoding dan DRM.'}
        </small>
      </div>
    </div>
  );
});

export default VideoDrmUpload;
